import clsx from 'clsx';
import React from 'react';

import { FileEntry, View } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { Authorization } from '@/src/lib/authorization/authorization';

import ArchiveFileToLibrary from './ArchiveFileToLibrary/ArchiveFileToLibrary';
import CopyFileUrl from './CopyFileUrl/CopyFileUrl';
import CreateTask from './CreateTask/CreateTask';
import DeleteFile from './DeleteFile/DeleteFile';
import DownloadFile from './DownloadFile/DownloadFile';
import MarkReadFile from './EllipsisActions/MarkReadFile/MarkReadFile';
import RenameFile from './EllipsisActions/RenameFile/RenameFile';
import TagFileWorkstream from './EllipsisActions/TagFileWorkstream/TagFileWorkstream';
import MarkMiscellaneous from './MarkMiscellaneous/MarkMiscellaneous';
import PermanentDeleteFile from './PermanentDeleteFile/PermanentDeleteFile';
import RecordFile from './RecordFile/RecordFile';
import RestoreFile from './RestoreFile/RestoreFile';

import EllipsisActionsContainer from '../../../../../Elements/EllipsisActionsContainer/EllipsisActionsContainer';
import MaximizeMinimizeScreen from '../../../Common/Actions/MaximizeMinimizeScreen/MaximizeMinimizeScreen';
import SidebarItem from '../../../Common/SidebarEntryPanel/SidebarItem/SidebarItem';
import SidebarSection from '../../../Common/SidebarEntryPanel/SidebarSection/SidebarSection';
import { useFileEntryViewer } from '../../store/fileEntryViewer.store';
import SidebarEntryPanel from '../SidebarEntryPanel/SidebarEntryPanel';

export type SidebarSettings = {
  isDeleteEnabled: boolean;
  canMaximizeMinimize: boolean;
};

type SidebarActionsProps = {
  fileEntry: FileEntry;
  sidebarSettings: SidebarSettings;
};

const SidebarActions = ({ fileEntry, sidebarSettings }: SidebarActionsProps) => {
  const { toggleIsFullScreen, isFullScreen } = useFileEntryViewer();
  const { view } = useInboxSearchParams();
  const isDeleted = view == View.Trash;
  const isMainSectionEnabled =
    (fileEntry.permissions.record ||
      fileEntry.permissions.archive ||
      fileEntry.permissions.createTask ||
      fileEntry.permissions.misc) &&
    !isDeleted;
  const isEllipsisEnabled =
    (fileEntry.permissions.read ||
      fileEntry.permissions.tagWorkstream ||
      fileEntry.permissions.rename) &&
    !isDeleted;

  return (
    <SidebarEntryPanel>
      <SidebarItem
        className={clsx('tw-min-h-[51px]', 'tw-bg-white')}
        paddingClasses={clsx('tw-px-[10px]')}
        marginClasses={clsx('tw-ml-[-1px]')}
      >
        {sidebarSettings.canMaximizeMinimize && (
          <MaximizeMinimizeScreen
            isFullScreen={isFullScreen}
            onToggleIsFullScreen={toggleIsFullScreen}
          />
        )}
      </SidebarItem>

      <Authorization policyCheck={isMainSectionEnabled}>
        <SidebarSection>
          <Authorization policyCheck={fileEntry.permissions.record}>
            <SidebarItem>
              <RecordFile fileEntry={fileEntry} />
            </SidebarItem>
          </Authorization>

          <Authorization policyCheck={fileEntry.permissions.archive}>
            <SidebarItem>
              <ArchiveFileToLibrary fileEntry={fileEntry} />
            </SidebarItem>
          </Authorization>

          <Authorization policyCheck={fileEntry.permissions.createTask}>
            <SidebarItem>
              <CreateTask fileEntry={fileEntry} />
            </SidebarItem>
          </Authorization>

          <Authorization policyCheck={fileEntry.permissions.misc}>
            <SidebarItem>
              <MarkMiscellaneous inboxEntryId={fileEntry.id} isMisc={fileEntry.isMisc} />
            </SidebarItem>
          </Authorization>
        </SidebarSection>
      </Authorization>

      <Authorization policyCheck={!isDeleted}>
        <SidebarSection>
          <Authorization policyCheck={fileEntry.permissions.copyFileUrl}>
            <SidebarItem>
              <CopyFileUrl fileEntry={fileEntry} />
            </SidebarItem>
          </Authorization>

          <SidebarItem>
            <Authorization policyCheck={fileEntry.permissions.download}>
              <DownloadFile fileEntry={fileEntry} />
            </Authorization>
          </SidebarItem>

          <SidebarItem>
            <EllipsisActionsContainer disabled={!isEllipsisEnabled} isActive={''}>
              <Authorization policyCheck={fileEntry.permissions.read}>
                <MarkReadFile isRead={fileEntry.isRead} />
              </Authorization>
              <Authorization policyCheck={fileEntry.permissions.tagWorkstream}>
                <TagFileWorkstream fileEntry={fileEntry} />
              </Authorization>
              <Authorization policyCheck={fileEntry.permissions.rename}>
                <RenameFile fileEntry={fileEntry} />
              </Authorization>
              {/* <Authorization policyCheck={fileEntry.permissions.duplicate}>
              <DuplicateFile />
            </Authorization> */}
            </EllipsisActionsContainer>
          </SidebarItem>
        </SidebarSection>
      </Authorization>

      {/* <Authorization policyCheck={canDelete}> */}
      {!isDeleted && (
        <SidebarSection>
          <Authorization policyCheck={fileEntry.permissions.trash}>
            <SidebarItem>
              <DeleteFile fileEntry={fileEntry} enabled={sidebarSettings.isDeleteEnabled} />
            </SidebarItem>
          </Authorization>
        </SidebarSection>
      )}
      {isDeleted && (
        <React.Fragment>
          <Authorization policyCheck={fileEntry.permissions.restore}>
            <SidebarSection>
              <SidebarItem>
                <RestoreFile fileEntry={fileEntry} enabled={sidebarSettings.isDeleteEnabled} />
              </SidebarItem>
            </SidebarSection>
          </Authorization>

          <Authorization policyCheck={fileEntry.permissions.deletePermanent}>
            <SidebarSection>
              <SidebarItem>
                <PermanentDeleteFile
                  fileEntry={fileEntry}
                  enabled={sidebarSettings.isDeleteEnabled}
                />
              </SidebarItem>
            </SidebarSection>
          </Authorization>
        </React.Fragment>
      )}
      {/* </Authorization> */}
    </SidebarEntryPanel>
  );
};

export default SidebarActions;
